
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useInterval } from "@/hooks/use-interval";
import { useCarouselImages } from "@/hooks/useCarouselImages";

// Fallback images in case database is empty
const fallbackImages = [
  {
    url: "https://images.unsplash.com/photo-1600618528240-fb9fc964b853?q=80&w=1200&auto=format&fit=crop",
    alt: "Serene wellness space for holistic therapy"
  },
  {
    url: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=1200&auto=format&fit=crop",
    alt: "Holistic therapy session environment"
  },
  {
    url: "https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?q=80&w=1200&auto=format&fit=crop",
    alt: "Professional wellness consultation"
  },
  {
    url: "https://images.unsplash.com/photo-1529693662653-9d480530a697?q=80&w=1200&auto=format&fit=crop",
    alt: "Peaceful nature setting for therapeutic relaxation"
  }
];

const HeroSection = () => {
  const { user, activeRole } = useAuth();
  const { t } = useTranslation('homepage');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { images: carouselImages, loading } = useCarouselImages();

  // Use carousel images from database or fallback to hardcoded ones
  const images = carouselImages.length > 0 
    ? carouselImages.map(img => ({ url: img.file_path, alt: img.alt_text }))
    : fallbackImages;

  // Auto advance carousel every 8 seconds
  useInterval(() => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, 8000);

  return (
    <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden bg-warm-50">
      <div className="container mx-auto px-4 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="space-y-8 md:pr-8 order-2 md:order-1">
            <h1 className="text-4xl md:text-5xl lg:text-7xl leading-tight text-warm-900 font-brand">
              {t('heroTitle')}
            </h1>
            <p className="text-lg md:text-xl text-warm-600 leading-relaxed max-w-xl">
              {t('heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Button asChild size="lg" className="bg-gradient-brand text-white hover:opacity-90 rounded-full px-8 py-3 font-semibold shadow-lg">
                <Link to="/booking-choice">
                  {t('bookAppointment', { ns: 'navigation' })} <ArrowRight size={16} className="ml-2" />
                </Link>
              </Button>
              {user ? (
                <Button asChild size="lg" variant="outline" className="border-brand-500 text-brand-700 hover:bg-brand-50 rounded-full px-8">
                  <Link to={activeRole === 'client' ? '/client-dashboard' : '/practitioner-dashboard'}>
                    {t('myDashboard', { ns: 'navigation' })}
                  </Link>
                </Button>
              ) : (
                <Button asChild size="lg" variant="outline" className="border-brand-500 text-brand-700 hover:bg-brand-50 rounded-full px-8">
                  <Link to="/become-practitioner">
                    {t('joinAsPractitioner')}
                  </Link>
                </Button>
              )}
            </div>
          </div>
          <div className="relative h-[300px] md:h-[400px] lg:h-[500px] rounded-2xl overflow-hidden shadow-2xl order-1 md:order-2">
            {/* Image Carousel */}
            <div className="relative w-full h-full">
              {!loading && images.map((image, index) => (
                <div 
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img 
                    src={image.url} 
                    alt={image.alt} 
                    className="w-full h-full object-cover rounded-2xl"
                    loading={index === 0 ? "eager" : "lazy"}
                    crossOrigin="anonymous"
                    sizes="(max-width: 768px) 100vw, 50vw"
                    fetchPriority={index === 0 ? "high" : "low"}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
